import jp from 'jsonpath'
import { JSONSchema7, JSONSchema7Definition } from 'json-schema'
import { createSchemaUtils, getClosestMatchingOption } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'

import { AiExecuteResult, StructuredSummaryResult } from '../../models/AiResult'
import { BaseAIAgentExecutionResult } from '../../models/AIAgent'

export const DEFAULT_IGNORE_FIELDS = ['id', 'Id']

export const IGNORE_FIELDS: { [key: string]: string[] } = { precisefp: ['row_id'] }

export const REMOVE_FIELDS: { [key: string]: string[] } = { wealthbox: ['custom_fields'], redtail: ['udfs'] }

export const LABEL_FIELD_KEY_MAP: { [key: string]: string } = { emoney: 'name', precisefp: 'name', orion: 'name', holistiplan: 'name', rightcapital: 'name' }

export const SEARCH_KEY_MAP: { [key: string]: string } = { precisefp: 'row_id' }

export const DEFAULT_SEARCH_KEY = 'id'

export const SEPARATOR = '.'

export type PanelStatus = 'success' | 'error' | 'warning'
export type ChildStatus = 'success' | 'error'

export const getFilteredContactValues = (
  serviceId: string,
  contactValues: { [key: string]: any },
  previousContactValues: {
    [key: string]: any
  } | null,
) => {
  return Object.keys(contactValues).reduce((acc: { [key: string]: any }, k) => {
    const removeFields = REMOVE_FIELDS[serviceId] ?? []
    if (!removeFields.includes(k) && JSON.stringify(previousContactValues ? previousContactValues[k] : null) !== JSON.stringify(contactValues[k])) {
      acc[k] = contactValues[k]
    }
    return acc
  }, {})
}

export const filterHiddenFields = (serviceId: string, [key, value]: [string, any]) => {
  const ignoreFields = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS

  return value !== null && !ignoreFields.includes(key)
}

export const filterFieldsWithoutSchema =
  (schema: any) =>
  ([key]: [string, any]) =>
    schema?.properties?.hasOwnProperty(key)

export const getAllKeysInData = (data: any, acc: string): string[] => {
  if (data === null) {
    return []
  }

  if (typeof data === 'object') {
    if (Array.isArray(data)) {
      return data.map((item, itemIndex) => getAllKeysInData(item, `${acc}.${itemIndex}`)).flat()
    }
    return Object.entries(data)
      .map(([key, value]) => getAllKeysInData(value, `${acc}.${key}`))
      .flat()
  }

  return [acc]
}

export const getJsonPathFromPathParts = (pathParts: string[]) => {
  return pathParts.reduce<string>((jsonPath, part, index) => {
    if (index === 0) {
      return `$['${part}']`
    }
    if (isNaN(+part)) {
      return `${jsonPath}['${part}']`
    } else {
      return `${jsonPath}[${part}]`
    }
  }, '')
}

export const filterDataByKeys = (obj: any, keys: string[]): any => {
  const result: { [key: string]: any } = {}
  keys.forEach((key) => {
    const pathParts = key.split(SEPARATOR)
    const jsonPath = getJsonPathFromPathParts(pathParts)
    const values = jp.query(obj, jsonPath)
    if (values.length > 0) {
      const pathSegments = jp.parse(jsonPath)
      let current = result
      pathSegments.forEach((segment, index) => {
        if (segment.expression.type === 'identifier' || segment.expression.type === 'numeric_literal' || segment.expression.type === 'string_literal') {
          const key = segment.expression.value

          if (index === pathSegments.length - 1) {
            current[key] = values[0]
          } else {
            if (!current[key]) {
              current[key] = typeof pathSegments[index + 1].expression.value === 'number' ? [] : {}
            }
            current = current[key]
          }
        }
      })
    }
  })
  return result
}

export const removeEmptyFields = (serviceId: string, obj: any) => {
  const ignoreFields = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS

  const hasAdditionalProperties = (subObj: any) => {
    return Object.keys(subObj).length > 1 || !Object.keys(subObj).every((k) => ignoreFields.includes(k))
  }

  const result: { [key: string]: any } = {}
  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      const filteredArray = obj[key]
        .map((item: any) => (typeof item === 'object' && item !== null ? removeEmptyFields(serviceId, item) : item))
        .filter(hasAdditionalProperties)
      if (filteredArray.length > 0) {
        result[key] = filteredArray
      }
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      const filteredObj = removeEmptyFields(serviceId, obj[key])
      if (hasAdditionalProperties(filteredObj)) {
        result[key] = filteredObj
      }
    } else {
      result[key] = obj[key]
    }
  }
  return result
}

const getItemSchema = (schema: JSONSchema7Definition | undefined) => {
  if (!schema || typeof schema === 'boolean' || !schema.items) {
    return
  }
  if (Array.isArray(schema.items)) {
    return schema.items[0]
  }
  return schema.items
}

const getObjectSchema = (schema: JSONSchema7Definition | undefined, value: any) => {
  if (!schema || typeof schema === 'boolean') {
    return schema
  }
  if (schema.oneOf) {
    const index = getClosestMatchingOption(validator, schema, value, schema.oneOf as JSONSchema7[], -1)
    if (index > -1) {
      return schema.oneOf[index]
    }
  }
  return schema
}

const getValueSchema = (schema: JSONSchema7Definition | undefined, key: string) => {
  if (!schema || typeof schema === 'boolean' || !schema.properties) {
    return
  }
  return schema.properties[key]
}

const getIsRequired = (schema: JSONSchema7Definition | undefined, key: string): boolean => {
  if (!schema || typeof schema === 'boolean') {
    return false
  }

  if (schema.required && Array.isArray(schema.required)) {
    if (schema.required.includes(key)) {
      return true
    }
  }

  if (schema.allOf && Array.isArray(schema.allOf)) {
    for (const subSchema of schema.allOf) {
      if (typeof subSchema === 'boolean') continue

      if (subSchema.then && typeof subSchema.then !== 'boolean') {
        const thenSchema = subSchema.then as JSONSchema7
        if (thenSchema.required && Array.isArray(thenSchema.required)) {
          if (thenSchema.required.includes(key)) {
            return true
          }
        }
      }
    }
  }

  return false
}

type OwnerFieldKey = 'type' | 'policyType'

export const OWNER_PATHS_BY_FIELD: Record<string, Partial<Record<OwnerFieldKey, string[]>>> = {
  cash: {
    type: ['owners'],
  },
  qualifiedretirement: {
    type: ['owners', 'primaryBeneficiaries'],
  },
  life: {
    policyType: ['owners', 'primaryBeneficiaries', 'insured', 'premiumPayer'],
  },
  realestates: {
    type: ['owners'],
  },
}

export const nullMatchingValues = (
  schema: JSONSchema7Definition | undefined,
  data: any,
  previousData: any,
  removeKeys: string[],
  ignoreKeys: string[],
  searchKey: string,
  serviceId: string,
  path: string[] = [],
): any => {
  if (data === null || data === undefined) {
    return null
  }

  if (previousData === null || previousData === undefined) {
    return data
  }

  if (Array.isArray(data)) {
    if (!Array.isArray(previousData)) {
      return data
    }

    const newData = data.reduce<any[]>((acc, item, index) => {
      const itemSchema = getItemSchema(schema)
      if (item[searchKey] === undefined || item[searchKey] === null) {
        const nulledItem = nullMatchingValues(itemSchema, item, previousData[index], removeKeys, ignoreKeys, searchKey, serviceId, [...path, String(index)])
        if (nulledItem === null) {
          return acc
        }
        return [...acc, nulledItem]
      }
      const previousItem = previousData.find((e: any) => e[searchKey] === item[searchKey])
      if (!previousItem) {
        return [...acc, item]
      }
      const nulledItem = nullMatchingValues(itemSchema, item, previousItem, removeKeys, ignoreKeys, searchKey, serviceId, [...path, String(index)])
      if (nulledItem === null) {
        return acc
      }
      return [...acc, nulledItem]
    }, [])

    if (!newData.length) {
      return null
    }

    return newData
  }

  if (typeof data === 'object') {
    if (typeof previousData !== 'object') {
      return data
    }

    const objectSchema = getObjectSchema(schema, data)

    const shouldIgnoreByOwnerPath = (key: string): boolean => {
      if (serviceId !== 'emoney') return false
      for (const assetKey of Object.keys(OWNER_PATHS_BY_FIELD)) {
        if (path.includes(assetKey)) {
          const ownerFieldMap = OWNER_PATHS_BY_FIELD[assetKey]
          if (!ownerFieldMap) continue
          const ownerFieldKeys = Object.keys(ownerFieldMap) as OwnerFieldKey[]
          for (const ownerFieldKey of ownerFieldKeys) {
            const nestedPaths = ownerFieldMap[ownerFieldKey] ?? []
            if (key === ownerFieldKey && nestedPaths.some((np) => path.includes(np))) {
              return true
            }
          }
        }
      }
      return false
    }

    const newData = Object.entries(data).reduce<{ [key: string]: any }>((acc, [key, value]) => {
      if (removeKeys.includes(key)) {
        return acc
      }
      if (ignoreKeys.includes(key)) {
        return { ...acc, [key]: value }
      }
      if (shouldIgnoreByOwnerPath(key)) {
        return acc
      }
      const valueSchema = getValueSchema(objectSchema, key)
      const nulledValue = nullMatchingValues(valueSchema, value, previousData[key], removeKeys, ignoreKeys, searchKey, serviceId, [...path, key])
      if (nulledValue === null) {
        return acc
      }
      return {
        ...acc,
        [key]: nulledValue,
      }
    }, {})

    if (Object.keys(newData).every((key) => ignoreKeys.includes(key))) {
      return null
    }

    return Object.entries(data).reduce<{ [key: string]: any }>((acc, [key, value]) => {
      const isValueRequired = getIsRequired(objectSchema, key)
      if (isValueRequired && acc[key] === undefined) {
        return {
          ...acc,
          [key]: value,
        }
      }
      return acc
    }, newData)
  }

  if (data === previousData) {
    return null
  }

  return data
}

export const filterIgnoreFieldsFromKeys = (serviceId: string, keys: string[]) => {
  const ignoreFields = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS

  return keys.filter((k) => {
    const paths = k.split(SEPARATOR)
    return !ignoreFields.includes(paths[paths.length - 1])
  })
}

export const getPreviousValue = (data: any, previousData: any, fieldIdParts: string[], searchKey: string): any => {
  if (!fieldIdParts.length) {
    return previousData
  }

  if (!data || !previousData) {
    return null
  }

  const part = fieldIdParts[0]
  if (!isNaN(+part) && Array.isArray(data) && !!data.length) {
    const newData = data[parseInt(part)]
    if (newData === undefined || newData[searchKey] === undefined || newData[searchKey] === null) {
      return null
    }

    const newPreviousData = previousData.find((e: any) => e[searchKey] === newData[searchKey])
    if (newPreviousData === undefined) {
      return null
    }

    return getPreviousValue(newData, newPreviousData, fieldIdParts.slice(1), searchKey)
  }

  const newData = data[part]
  const newPreviousData = previousData[part]
  return getPreviousValue(newData, newPreviousData, fieldIdParts.slice(1), searchKey)
}

export const getFieldIdParts = (rootFieldId: string, fieldId: string, separator: string = SEPARATOR) => {
  if (fieldId.startsWith(rootFieldId)) {
    const rootFieldIdParts = rootFieldId.split(separator)
    const fieldIdParts = fieldId.split(separator)
    return fieldIdParts.slice(rootFieldIdParts.length)
  }
  return fieldId.split(separator)
}

export const replaceNumbersWithAsterisk = (input: string): string => {
  const regex = /(?<=\.)\d+/g
  return input.replace(regex, '*')
}

export const getPanelRequiredSteps = (
  panel: { [key: string]: any },
  submittedValueKeys: string[],
): {
  [key: string]: string[]
} => {
  const allRequiredOperationSteps = panel?.['required_operation_steps'] ?? {}
  return submittedValueKeys.reduce((result: { [key: string]: string[] }, key) => {
    if (key in allRequiredOperationSteps) {
      result[key] = allRequiredOperationSteps[key]
    }
    return result
  }, {})
}

export const getFailedStepsInChild = (requiredSteps: string[], metadata: object): string[] => {
  const failedOperations = Object.keys(metadata || {})
  return requiredSteps.filter((step: string) => failedOperations.includes(step))
}

export const getChildStatus = (failedSteps: string[]): ChildStatus => {
  if (failedSteps.length) {
    return 'error'
  }
  return 'success'
}

const gatherChildSuccessfulFields = (
  contactValues: {
    [key: string]: any
  },
  parentField: string,
  fieldName: string,
  rootFieldId: string,
): string[] => {
  const prefix = `${rootFieldId}.${parentField}`
  const allFieldKeys = getAllKeysInData(contactValues, prefix)

  if (!allFieldKeys.length) {
    return []
  }
  if (fieldName === '_root') {
    return allFieldKeys
  }

  const patternToMatch = `${prefix}.${fieldName}`
  return allFieldKeys.filter((key) => replaceNumbersWithAsterisk(key).startsWith(patternToMatch))
}

export const getNumberOfChildren = (panel: { [key: string]: any }, submittedValueKeys: string[]): number => {
  const panelRequiredSteps = getPanelRequiredSteps(panel, submittedValueKeys)
  return Object.keys(panelRequiredSteps).length
}

export const gatherAllChildSuccessfulFields = (
  contactValues: { [key: string]: any },
  parentField: string,
  panel: {
    [key: string]: any
  },
  metadata: any,
  submittedValueKeys: string[],
  rootFieldId: string,
): string[] => {
  if (!metadata) {
    return []
  }

  const panelRequiredSteps = getPanelRequiredSteps(panel, submittedValueKeys)
  return Object.entries(panelRequiredSteps).reduce((acc: string[], [fieldName, value]): string[] => {
    const childRequiredSteps = value ?? []
    const childFailedSteps = getFailedStepsInChild(childRequiredSteps, metadata)
    if (getChildStatus(childFailedSteps) !== 'success') {
      return acc
    }

    return [...acc, ...gatherChildSuccessfulFields(contactValues, parentField, fieldName, rootFieldId)]
  }, [])
}

const getFailedSteps = (requiredSteps: string[], failedOperations: { [key: string]: any }): string[] => {
  return requiredSteps.filter((step) => step in failedOperations)
}

const getStatus = (failedSteps: string[]): ChildStatus => {
  if (failedSteps.length) {
    return 'error'
  }
  return 'success'
}

export const getFieldStatus = (
  requiredSteps: {
    [key: string]: any
  },
  fieldName: string,
  metadata: object,
): ChildStatus => {
  if (!(fieldName in requiredSteps) && !('_root' in requiredSteps)) {
    return 'error'
  }

  const failedOperations = metadata || {}
  const requiredStepsForField = requiredSteps[fieldName ?? '_root'] ?? []
  const failedSteps = getFailedSteps(requiredStepsForField, failedOperations)
  return getStatus(failedSteps)
}

export const getFieldErrorMessages = (
  requiredSteps: {
    [key: string]: string[]
  },
  fieldName: string,
  metadata: { [key: string]: string },
): string[] => {
  if (!metadata) {
    return []
  }

  const requiredStepsForField = requiredSteps[fieldName] ?? []
  return requiredStepsForField.map((key) => metadata[key]).filter(Boolean)
}

const getAllFieldIdParts = (fieldId: string): string[] => {
  return fieldId.split(SEPARATOR)
}

export const getFieldName = (fieldId: string): string => {
  const fieldIdParts = getAllFieldIdParts(fieldId)
  return replaceNumbersWithAsterisk(fieldIdParts.slice(2).join('.'))
}

export const getShouldDisplayStatusIcon = (
  fieldName: string,
  requiredSteps: {
    [key: string]: any
  },
  metadata: any,
): boolean => {
  return fieldName in requiredSteps && !('_root' in requiredSteps) && metadata && !!Object.keys(metadata).length
}

export function generateUiSchemaToHideEMoneyOwnerType(schema: Record<string, any>): Record<string, any> {
  const uiSchema: Record<string, any> = {}

  Object.entries(OWNER_PATHS_BY_FIELD).forEach(([fieldKey, typeToFields]) => {
    if (!typeToFields) return

    Object.entries(typeToFields).forEach(([fieldToHide, nestedKeys]) => {
      nestedKeys.forEach((nestedKey) => {
        const fieldSchema = schema.properties?.[fieldKey]
        if (fieldSchema?.type !== 'array' || !fieldSchema.items) return

        const itemSchema = fieldSchema.items
        const nestedArraySchema = itemSchema.properties?.[nestedKey]

        if (nestedArraySchema?.type === 'array' && nestedArraySchema.items?.properties?.[fieldToHide]) {
          uiSchema[fieldKey] = uiSchema[fieldKey] || {}
          uiSchema[fieldKey].items = uiSchema[fieldKey].items || {}
          uiSchema[fieldKey].items[nestedKey] = uiSchema[fieldKey].items[nestedKey] || {}
          uiSchema[fieldKey].items[nestedKey].items = uiSchema[fieldKey].items[nestedKey].items || {}

          uiSchema[fieldKey].items[nestedKey].items[fieldToHide] = { 'ui:widget': 'hidden' }
        }
      })
    })
  })

  return uiSchema
}

export function generateUiSchemaToDisableEMoneyFinancialPrioRank(): Record<string, any> {
  return {
    clientPriorities: {
      items: {
        rank: {
          'ui:disabled': true,
        },
      },
    },
    spousePriorities: {
      items: {
        rank: {
          'ui:disabled': true,
        },
      },
    },
  }
}

export const processContactUpdateSystemPresetResult = (
  serviceId: string,
  previousContactValues: {
    [key: string]: any
  } | null,
  aiExecuteResult: AiExecuteResult[],
) => {
  if (aiExecuteResult[0].result === null) {
    return null
  }
  const generatedContactValues = (aiExecuteResult[0].result as StructuredSummaryResult).result as {
    [key: string]: any
  }
  const filteredContactValues = getFilteredContactValues(serviceId, generatedContactValues, previousContactValues)
  if (!Object.keys(filteredContactValues).length) {
    return null
  }
  return filteredContactValues
}

export const processContactUpdateAgentResult = (
  serviceId: string,
  schema: { [key: string]: any },
  previousValues: {
    [key: string]: any
  } | null,
  aiAgentResult: BaseAIAgentExecutionResult,
) => {
  const removeKeys = REMOVE_FIELDS[serviceId] ?? []
  const ignoreKeys = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS
  const searchKey = SEARCH_KEY_MAP[serviceId] ?? DEFAULT_SEARCH_KEY

  const schemaUtils = createSchemaUtils(validator, schema)
  const resolvedSchema = schemaUtils.retrieveSchema(schema)

  const filteredContactValues = nullMatchingValues(resolvedSchema, aiAgentResult.result, previousValues, removeKeys, ignoreKeys, searchKey, serviceId)
  if (!filteredContactValues || !Object.keys(filteredContactValues).length) {
    return null
  }
  return filteredContactValues
}
